import type { MmObject } from "../api/mentalModels";

/**
 * Studio list shaping (spec 2026-11-03 §C). Input is the server's attention
 * order; every cut here keeps it.
 */

export const NEEDS_A_LOOK_MIN = 6; // strip shows only when the list has > 5
export const NEEDS_A_LOOK_COUNT = 3;

export interface MmAreaSection {
  /** Space id, or null for "Not in an area yet". */
  spaceId: string | null;
  name?: string;
  objects: MmObject[];
}

/** Object types present, in first-seen (attention) order. */
export function presentTypes(objects: MmObject[]): string[] {
  const seen: string[] = [];
  for (const o of objects) if (!seen.includes(o.objectType)) seen.push(o.objectType);
  return seen;
}

export function filterByType(objects: MmObject[], type: string | null): MmObject[] {
  return type ? objects.filter((o) => o.objectType === type) : objects;
}

/** Top of the attention order, only once the list is long enough to need it. */
export function needsALook(objects: MmObject[]): MmObject[] {
  return objects.length >= NEEDS_A_LOOK_MIN ? objects.slice(0, NEEDS_A_LOOK_COUNT) : [];
}

/**
 * Sections by area in the spaces' own order; a model filed in two areas shows
 * in both. Models with no (still-existing) area go last. Empty sections drop.
 */
export function groupByArea(
  objects: MmObject[],
  spaces: Array<{ _id: string; name: string }>
): MmAreaSection[] {
  const known = new Set(spaces.map((s) => s._id));
  const sections: MmAreaSection[] = spaces
    .map((s) => ({
      spaceId: s._id,
      name: s.name,
      objects: objects.filter((o) => (o.spaceIds ?? []).includes(s._id)),
    }))
    .filter((s) => s.objects.length > 0);
  const unfiled = objects.filter((o) => !(o.spaceIds ?? []).some((id) => known.has(id)));
  if (unfiled.length > 0) sections.push({ spaceId: null, objects: unfiled });
  return sections;
}

export type MmSource =
  | { kind: "note"; date?: string }
  | { kind: "takeaway" }
  | { kind: "quality" }
  | { kind: "created"; date?: string };

/** Where the model came from, for the quiet line under each row. */
export function sourceOf(o: MmObject): MmSource {
  if (o.context?.captureId) return { kind: "note", date: o.createdOn };
  if (o.liftedFrom?.entityType === "user_takeaway") return { kind: "takeaway" };
  if (o.liftedFrom?.entityType === "user_trait") return { kind: "quality" };
  return { kind: "created", date: o.createdOn };
}

/** "3 Nov" in the person's language; "" for missing/invalid dates. */
export function shortDate(value: string | number | undefined, language: string): string {
  if (value === undefined || value === null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString(language, { day: "numeric", month: "short" });
  } catch {
    return d.toLocaleDateString("en", { day: "numeric", month: "short" });
  }
}
